import React from 'react';
import { Box, Grid, Card, CardContent, Typography, CardActionArea, Container, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { motion } from 'framer-motion';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease-in-out',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '4rem',
    marginBottom: theme.spacing(3),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))',
  },
}));

const MotionGrid = motion(Grid);

const UserReport = () => {
  const theme = useTheme();
  const reports = [
    {
      id: 1,
      title: 'Match Report',
      icon: <SportsSoccerIcon />,
      description: 'Add and manage user match statistics and performance data',
      gradient: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
      url: 'https://forms.gle/fvSyBCoSPY2koJ5c8',
    },
    {
      id: 2,
      title: 'Training Report',
      icon: <FitnessCenterIcon />,
      description: 'Add and manage user training progress and development metrics',
      gradient: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
      url: 'https://forms.gle/87AasBZVxAx378Np7',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const handleCardClick = (url) => {
    window.open(url, '_blank');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            mb: 6,
            fontWeight: 'bold',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          Add Reports
        </Typography>
      </motion.div>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Grid
          container
          spacing={4}
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          sx={{
            maxWidth: '1200px',
            margin: '0 auto',
          }}
        >
          {reports.map((report) => (
            <MotionGrid
              item
              xs={12}
              sm={6}
              key={report.id}
              variants={itemVariants}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <CardActionArea 
                sx={{ width: '100%', maxWidth: '500px' }}
                onClick={() => handleCardClick(report.url)}
              >
                <StyledCard>
                  <StyledCardContent>
                    {report.icon}
                    <Typography
                      variant="h4"
                      component="h2"
                      gutterBottom
                      sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        background: report.gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {report.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      align="center"
                      sx={{
                        fontSize: '1.1rem',
                        lineHeight: 1.6,
                        maxWidth: '80%',
                      }}
                    >
                      {report.description}
                    </Typography>
                  </StyledCardContent>
                </StyledCard>
              </CardActionArea>
            </MotionGrid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default UserReport; 